import { useState, useEffect } from "react";
import { PageHero, SectionLabel, PillButton } from "../components/ui-kit";
import { Reveal } from "../components/motion";
import { postApplication, getFaqs } from "../lib/api";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Check, ArrowRight, Loader2 } from "lucide-react";

const perks = ["Fully personalised training program", "Custom macro-based nutrition plan", "Weekly check-ins & adjustments", "Video form reviews", "24/7 messaging support", "Habit & accountability coaching"];
const programs = ["Fat Loss", "Muscle Gain", "Strength", "Body Recomposition", "General Fitness"];

export default function Coaching() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", age: "", goal: "", experience: "Beginner", program: "Fat Loss", budget: "", message: "" });

  useEffect(() => { getFaqs().then(setFaqs).catch(() => {}); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await postApplication(form);
      toast.success("Application received! Manav will reach out within 24-48 hours.");
      setForm({ name: "", email: "", phone: "", age: "", goal: "", experience: "Beginner", program: "Fat Loss", budget: "", message: "" });
    } catch {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-secondary/50 border border-border rounded-xl px-4 py-3 text-sm outline-none focus:border-accent transition-colors";

  return (
    <div data-testid="coaching-page">
      <PageHero label="Coaching" title={<>Apply for <span className="text-accent">1:1 coaching.</span></>}
        subtitle="Limited spots available. Fill out the application below and I'll personally review it within 24-48 hours." />

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel index="01">What You Get</SectionLabel>
              <h2 className="text-3xl font-black tracking-tight mb-8">Everything you need to transform.</h2>
              <ul className="space-y-4">
                {perks.map((p) => (
                  <li key={p} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-accent flex items-center justify-center shrink-0 mt-0.5"><Check className="w-4 h-4 text-white" /></span>
                    <span className="text-muted-foreground">{p}</span>
                  </li>
                ))}
              </ul>
              <div className="glass rounded-2xl p-6 mt-10">
                <p className="text-sm text-muted-foreground">🔥 Only a limited number of coaching spots open each month to guarantee premium quality attention.</p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} data-testid="application-form" id="coaching-application" className="glass rounded-3xl p-8 md:p-10 space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <input required placeholder="Full Name *" value={form.name} onChange={set("name")} data-testid="app-name" className={inputCls} />
                  <input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="app-email" className={inputCls} />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <input required placeholder="Phone / WhatsApp *" value={form.phone} onChange={set("phone")} data-testid="app-phone" className={inputCls} />
                  <input placeholder="Age" value={form.age} onChange={set("age")} data-testid="app-age" className={inputCls} />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <select value={form.program} onChange={set("program")} data-testid="app-program" className={inputCls}>
                    {programs.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <select value={form.experience} onChange={set("experience")} data-testid="app-experience" className={inputCls}>
                    {["Beginner", "Intermediate", "Advanced"].map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
                <input required placeholder="Your #1 goal right now *" value={form.goal} onChange={set("goal")} data-testid="app-goal" className={inputCls} />
                <textarea rows={4} placeholder="Tell me about your current situation & what's holding you back..." value={form.message} onChange={set("message")} data-testid="app-message" className={inputCls} />
                <button type="submit" disabled={loading} data-testid="app-submit"
                  className="w-full rounded-full bg-accent text-white py-4 font-semibold flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-60">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <>Submit Application <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 border-t border-border">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <SectionLabel index="02">Investment</SectionLabel>
          <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-4">Ready to see the plans?</h2>
          <p className="text-muted-foreground mb-8">Explore our three coaching tiers — Training Program, Guided Coaching and Elite 1-on-1 — and pick the level of support that fits your goals.</p>
          <PillButton to="/pricing" testid="coaching-see-pricing">See Pricing Plans <ArrowRight className="w-4 h-4 ml-2" /></PillButton>
        </div>
      </section>

      <section className="py-20 border-t border-border">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <SectionLabel index="03">FAQ</SectionLabel>
          <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-10">Questions, answered.</h2>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="glass rounded-2xl px-6 border-none">
                <AccordionTrigger className="text-left font-heading font-semibold hover:no-underline hover:text-accent">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
